

import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { CardReveal } from './CardReveal';
import { ParticleEffects, ConfettiEffect, PRIME_COLORS, LEGENDARY_COLORS, DEFAULT_COLORS } from './ParticleEffects';
import { asset } from '@/lib/asset';

// ============================================================================
// THE MINT Pack Opening Scene
// ============================================================================

type Rarity = 'PROSPECT' | 'ROOKIE' | 'VETERAN' | 'ALL_STAR' | 'GOAT';

interface Card {
  playerId: string;
  playerName: string;
  team: string;
  position: string;
  rarity: Rarity;
  imageUrl: string | null;
}

type Stage = 'intro' | 'opening' | 'reveal' | 'complete';

interface PackOpeningSceneProps {
  packId: string;
  packName: string;
  cards: Card[];
  onComplete: () => void;
}

export function PackOpeningScene({
  packId,
  packName,
  cards,
  onComplete,
}: PackOpeningSceneProps) {
  const [stage, setStage] = useState<Stage>('intro');
  const [revealedCount, setRevealedCount] = useState(0);
  const [showParticles, setShowParticles] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const bestRarity = cards.reduce((best, card) => {
    const order: Rarity[] = ['PROSPECT', 'ROOKIE', 'VETERAN', 'ALL_STAR', 'GOAT'];
    return order.indexOf(card.rarity) > order.indexOf(best) ? card.rarity : best;
  }, 'PROSPECT' as Rarity);

  const particleColors =
    bestRarity === 'GOAT' ? PRIME_COLORS :
    bestRarity === 'ALL_STAR' ? LEGENDARY_COLORS :
    DEFAULT_COLORS;

  const handleOpenPack = () => {
    setStage('opening');
    setShowParticles(true);

    // Transition to reveal after opening animation
    setTimeout(() => {
      setStage('reveal');
    }, 2000);
  };

  const handleCardReveal = useCallback((rarity: Rarity) => {
    setRevealedCount((prev) => prev + 1);

    // Trigger extra effects for rare+ cards
    if (rarity === 'ALL_STAR' || rarity === 'GOAT') {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    }
  }, []);

  const handleRevealAll = () => {
    setRevealedCount(cards.length);
    setShowParticles(true);

    if (bestRarity === 'ALL_STAR' || bestRarity === 'GOAT') {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    }

    setTimeout(() => {
      setStage('complete');
      setShowParticles(false);
    }, 1000);
  };

  const allRevealed = revealedCount >= cards.length;

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      {/* Particle Effects */}
      <ParticleEffects
        isActive={showParticles}
        intensity={bestRarity === 'GOAT' ? 'high' : bestRarity === 'ALL_STAR' ? 'medium' : 'low'}
        colors={particleColors}
      />
      <ConfettiEffect isActive={showConfetti} />

      {/* Intro Stage - Pack Display */}
      <AnimatePresence mode="wait">
        {stage === 'intro' && (
          <motion.div
            key="intro"
            className="flex-1 flex flex-col items-center justify-center px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative mb-8"
              animate={{
                y: [0, -10, 0],
                rotateY: [0, 5, 0, -5, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              <div className="w-48 h-64 rounded-xl bg-parchment border-2 border-parchment-border shadow-2xl flex flex-col items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-[#8B3A3A] ring-4 ring-[#6B2A2A] flex items-center justify-center mb-3">
                  <Image src={asset('logo.webp')} alt="Seal" width={36} height={36} className="opacity-80 brightness-200" />
                </div>
                <p className="font-serif font-bold text-surface text-sm">{packName}</p>
                <p className="series-text mt-1">{cards.length} Cards Struck</p>
              </div>
              <motion.div
                className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-gold/30 to-verdigris/30 blur-xl"
                animate={{
                  opacity: [0.3, 0.6, 0.3],
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                }}
              />
            </motion.div>

            <h2 className="text-3xl font-bold font-serif text-cream mb-2">{packName}</h2>
            <p className="text-warm-gray mb-8">{cards.length} cards inside</p>

            <Button size="xl" onClick={handleOpenPack}>
              <Sparkles className="h-5 w-5" />
              Break the Seal
            </Button>
          </motion.div>
        )}

        {/* Opening Animation */}
        {stage === 'opening' && (
          <motion.div
            key="opening"
            className="flex-1 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative"
              animate={{
                scale: [1, 1.2, 0],
                rotateY: [0, 180, 360],
              }}
              transition={{
                duration: 1.5,
                times: [0, 0.5, 1],
              }}
            >
              <div className="w-48 h-64 rounded-xl bg-parchment border-2 border-parchment-border shadow-2xl" />
            </motion.div>
          </motion.div>
        )}

        {/* Reveal Stage - Cards Grid */}
        {stage === 'reveal' && (
          <motion.div
            key="reveal"
            className="flex-1 flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="p-4 border-b border-surface-3">
              <div className="max-w-7xl mx-auto flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-cream">{packName}</h2>
                  <p className="text-sm text-warm-gray">
                    {revealedCount} / {cards.length} revealed
                  </p>
                </div>
                {!allRevealed && (
                  <Button variant="outline" onClick={handleRevealAll}>
                    Reveal All
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                )}
                {allRevealed && (
                  <Button onClick={() => setStage('complete')}>
                    Continue
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-8">
              <div className="flex flex-wrap justify-center gap-6 max-w-5xl">
                {cards.map((card, index) => (
                  <CardReveal
                    key={`${card.playerId}-${index}`}
                    playerName={card.playerName}
                    team={card.team}
                    position={card.position}
                    rarity={card.rarity}
                    imageUrl={card.imageUrl}
                    index={index}
                    isRevealed={revealedCount > index}
                    onReveal={() => handleCardReveal(card.rarity)}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Complete Stage - Summary */}
        {stage === 'complete' && (
          <motion.div
            key="complete"
            className="flex-1 flex flex-col items-center justify-center px-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="mb-8"
            >
              <div className="h-20 w-20 rounded-full bg-verdigris/20 flex items-center justify-center">
                <Sparkles className="h-10 w-10 text-verdigris" />
              </div>
            </motion.div>

            <h2 className="text-3xl font-bold text-cream mb-2">Pack Complete!</h2>
            <p className="text-warm-gray mb-8">
              {cards.length} cards added to your collection
            </p>

            {/* Card Summary */}
            <div className="flex gap-4 mb-8">
              {Object.entries(
                cards.reduce((acc, card) => {
                  acc[card.rarity] = (acc[card.rarity] || 0) + 1;
                  return acc;
                }, {} as Record<string, number>)
              ).map(([rarity, count]) => (
                <div
                  key={rarity}
                  className={cn(
                    'px-4 py-2 rounded-xl font-mono',
                    rarity === 'GOAT' ? 'bg-gold/20 text-gold border border-gold/30' :
                    rarity === 'ALL_STAR' ? 'bg-[#059669]/20 text-[#059669] border border-[#059669]/30' :
                    rarity === 'VETERAN' ? 'bg-[#DC2626]/20 text-[#DC2626] border border-[#DC2626]/30' :
                    rarity === 'ROOKIE' ? 'bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/30' :
                    'bg-[#9BA3AF]/20 text-[#9BA3AF] border border-[#9BA3AF]/30'
                  )}
                >
                  <span className="font-bold">{count}</span> {rarity}
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <Link href="/collection">
                <Button variant="outline" size="lg">
                  View Collection
                </Button>
              </Link>
              <Link href="/store">
                <Button size="lg">
                  <Sparkles className="h-5 w-5" />
                  Open Another
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
